import React from "react";
import "./BookingStageTracker.css";
import {
  getWorkflowProgress,
  getStageStatusClass,
  STAGE_STATUSES,
} from "../utils/bookingWorkflow";

const ProgressRing = ({ percent, size = 36 }) => {
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="progress-ring-wrap" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="progress-ring" aria-hidden="true">
        <circle
          className="progress-ring-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
        />
        <circle
          className="progress-ring-fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="progress-ring-label">{percent}%</span>
    </div>
  );
};

const BookingStageTracker = ({
  booking,
  canEdit = false,
  onStageChange,
  updatingStageId = null,
}) => {
  const stages = booking?.workflow_stages || [];
  const progress =
    booking?.workflow_progress || getWorkflowProgress(stages);

  if (!stages.length) {
    return null;
  }

  return (
    <details className="booking-stage-tracker">
      <summary className="stage-tracker-summary">
        <ProgressRing percent={progress.percent} />
        <div className="stage-summary-text">
          <span className="stage-summary-title">Progress</span>
          <span className="stage-summary-sub">
            {progress.completed}/{progress.total} done
          </span>
        </div>
        <div className="stage-summary-chips">
          <span className="mini-chip chip-pending">{progress.pending}P</span>
          <span className="mini-chip chip-progress">{progress.inProgress}A</span>
          <span className="mini-chip chip-done">{progress.completed}D</span>
        </div>
      </summary>

      <div className="stage-tracker-body">
        <div
          className="stage-progress-bar-wrap"
          role="progressbar"
          aria-valuenow={progress.percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="stage-progress-bar-fill"
            style={{ width: `${progress.percent}%` }}
          />
        </div>

        <ul className="stage-list">
          {stages.map((stage, index) => (
            <li
              key={stage.stage_id}
              className={`stage-row ${getStageStatusClass(stage.status)}`}
            >
              <span className="stage-index">{index + 1}</span>
              <span className="stage-label" title={stage.label}>
                {stage.label}
              </span>
              {canEdit ? (
                <select
                  className={`stage-status-select ${getStageStatusClass(stage.status)}`}
                  value={stage.status}
                  disabled={updatingStageId === stage.stage_id}
                  aria-label={`Update status for ${stage.label}`}
                  onChange={(event) =>
                    onStageChange(stage.stage_id, event.target.value)
                  }
                >
                  {STAGE_STATUSES.map((statusOption) => (
                    <option key={statusOption} value={statusOption}>
                      {statusOption}
                    </option>
                  ))}
                </select>
              ) : (
                <span
                  className={`stage-status-badge ${getStageStatusClass(stage.status)}`}
                >
                  {stage.status}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
};

export default BookingStageTracker;
